import { z } from 'zod';
import { localeSchema, productIdSchema, timeZoneSchema, usernameSchema } from './common.js';

/** The providers an OAuth sign-in can run against. Email is not one of them. */
export const authProviderSchema = z.enum(['google', 'microsoft']);
export type AuthProvider = z.infer<typeof authProviderSchema>;

/**
 * How an identity on an account was established.
 *
 * Wider than authProviderSchema, and deliberately a separate list: `email` is
 * proof of an address rather than a provider we redirect to, and keeping it out
 * of the OAuth enum is what stops `/auth/email/start` being matched by the
 * `/auth/:provider/start` route it has nothing to do with.
 */
export const identityProviderSchema = z.enum(['google', 'microsoft', 'email']);
export type IdentityProvider = z.infer<typeof identityProviderSchema>;

export const subscriptionStatusSchema = z.enum([
  'none',
  'trialing',
  'active',
  'past_due',
  'canceled',
  'unpaid',
  'incomplete',
]);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

/** What the SPA receives for the signed-in user. Never includes tokens or internal ids of providers. */
export const currentUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  /**
   * The business this account trades as, where that is not simply the person.
   * Null until somebody sets it, and everything that would show it shows the
   * person's name instead, so an account that never sets one is unchanged.
   */
  organizationName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  username: z.string().nullable(),
  locale: localeSchema,
  timezone: z.string(),
  enabledProducts: z.array(productIdSchema),
  signupProduct: productIdSchema,
  providers: z.array(identityProviderSchema),
  billing: z.object({
    status: subscriptionStatusSchema,
    /** A subscription exists, whatever state it is in. */
    subscribed: z.boolean(),
    /**
     * Derived: subscriber-only features are unlocked right now. True while a
     * subscription is in good standing and through the past-due grace window.
     * The free plan is never locked, so there is no opposite of this for the
     * basic features.
     */
    paid: z.boolean(),
    currentPeriodEndsAt: z.string().datetime().nullable(),
    cancelAtPeriodEnd: z.boolean(),
    /** Paid features given rather than bought, until this date. Usually null. */
    compedUntil: z.string().datetime().nullable().default(null),
    configured: z.boolean(),
  }),
  stripeConnect: z
    .object({
      accountId: z.string(),
      /** Which Stripe dashboard the host should be sent to. */
      livemode: z.boolean(),
      chargesEnabled: z.boolean(),
      payoutsEnabled: z.boolean(),
      defaultCurrency: z.string().nullable(),
      country: z.string().nullable(),
    })
    .nullable(),
  createdAt: z.string().datetime(),
});
export type CurrentUser = z.infer<typeof currentUserSchema>;

export const updateAccountSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    /** Empty clears it, which puts the person's own name back everywhere. */
    organizationName: z.string().trim().max(80),
    username: usernameSchema,
    locale: localeSchema,
    timezone: timeZoneSchema,
  })
  .partial();
export type UpdateAccountInput = z.infer<typeof updateAccountSchema>;

export const setProductsSchema = z.object({
  enabledProducts: z.array(productIdSchema).min(1),
});
export type SetProductsInput = z.infer<typeof setProductsSchema>;

export const deleteAccountSchema = z.object({
  /** User must type their email to confirm. */
  confirmEmail: z.string().trim().toLowerCase(),
});

/**
 * One line of account history.
 *
 * `detail` is resolved on the server — an email address, a provider name, the
 * two people a booking moved between — because the shape of it differs per
 * action and the screen should not have to know twenty of them. The sentence
 * around it is translated from `action` like any other string.
 */
export const auditEntrySchema = z.object({
  id: z.string(),
  action: z.string(),
  at: z.string(),
  detail: z.string().nullable(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

export const auditPageSchema = z.object({
  items: z.array(auditEntrySchema),
  /** The `at` of the oldest row returned; absent when there is no more. */
  nextCursor: z.string().nullable(),
});
export type AuditPage = z.infer<typeof auditPageSchema>;
